import { ChevronLeft, ChevronRight } from 'lucide-react';
import { useLocale } from '@/shared/lib/i18n/locale-context';
import { Button } from '@/shared/ui/Button';

/**
 * Moving between pages of a list.
 *
 * ⚠️ **Nothing in this panel paginated until 22 August 2026, and the API always did.** Every list
 * endpoint returns a `PagedList` with `totalCount`, `page` and `hasNextPage`, and every screen threw
 * all three away and rendered the first page. §23.10 puts 3,000+ volunteers in this system: the
 * review queue showed the first twenty and there was no way to reach volunteer twenty-one, on the
 * screen that decides whether a person can be sent to a search. Not a cosmetic gap.
 *
 * ⚠️ **The arrows follow the reading direction, not the screen.** In Arabic "next" points left. A
 * chevron hard-coded to point right is an arrow that says "back" to half the people using it, which
 * is why the icon is chosen from the locale rather than from a fixed prop.
 *
 * ⚠️ Rendered as nothing when there is one page. A disabled pair of arrows under a five-row table
 * is furniture that teaches an operator to stop looking at the bottom of lists.
 */
export function Pagination({
  page,
  pageSize,
  totalCount,
  onPageChange,
}: {
  page: number;
  pageSize: number;
  totalCount: number;
  onPageChange: (page: number) => void;
}) {
  const { t, locale } = useLocale();

  const totalPages = Math.max(1, Math.ceil(totalCount / pageSize));
  if (totalPages <= 1) return null;

  const first = (page - 1) * pageSize + 1;
  const last = Math.min(page * pageSize, totalCount);

  const rtl = locale === 'ar';
  const Previous = rtl ? ChevronRight : ChevronLeft;
  const Next = rtl ? ChevronLeft : ChevronRight;

  return (
    <nav
      aria-label={t.pagination.label}
      className="flex flex-wrap items-center justify-between gap-3 border-t border-gray-100 px-5 py-4"
    >
      {/* ⚠️ "1–20 of 3,412", not "page 1 of 171". An operator working a queue needs to know how much
          is left to work, and a page number answers a different question. */}
      <p className="text-theme-sm text-gray-500">
        <span className="tabular-nums">{first}</span>
        {'–'}
        <span className="tabular-nums">{last}</span>
        {` ${t.pagination.of} `}
        <span className="tabular-nums font-medium text-gray-800">{totalCount}</span>
      </p>

      <div className="flex items-center gap-2">
        <Button
          variant="ghost"
          size="sm"
          disabled={page <= 1}
          onClick={() => onPageChange(page - 1)}
        >
          <Previous aria-hidden className="size-4" />
          {t.pagination.previous}
        </Button>

        <span className="text-theme-xs tabular-nums text-gray-500">
          {page} / {totalPages}
        </span>

        <Button
          variant="ghost"
          size="sm"
          disabled={page >= totalPages}
          onClick={() => onPageChange(page + 1)}
        >
          {t.pagination.next}
          <Next aria-hidden className="size-4" />
        </Button>
      </div>
    </nav>
  );
}
